// How each chart looks, set by an Admin on Administration -> Appearance ->
// Charts - the same kind of choices as Excel's "Format Chart" pane. Only
// what the Admin changed is saved; everything else falls back to the
// chart's own defaults in chartRegistry.ts. Saved on the server
// (GET/PUT /system-settings/appearance/charts) and cached in this browser.
import { useSyncExternalStore } from 'react';
import apiClient from '../../services/api';

export type SeriesType = 'bar' | 'line' | 'area' | 'points';
export type LabelMode = 'auto' | 'all' | 'last' | 'none';
export type LabelPosition = 'auto' | 'top' | 'bottom' | 'center' | 'insideTop';

export interface AxisSettings {
  show?: boolean;
  title?: string;
  min?: number | null; // null/undefined = automatic
  max?: number | null;
  step?: number | null; // major unit
  labelEvery?: number; // label every Nth step (the rest are fine lines)
  decimals?: number | null;
  units?: 'none' | 'thousands' | 'lakhs' | 'crores';
  prefix?: string;
  suffix?: string;
  log?: boolean;
  reverse?: boolean;
  clip?: boolean; // true = keep the fixed min/max even when data goes past them
}

export interface SeriesSettings {
  name?: string;
  type?: SeriesType;
  color?: string;
  hidden?: boolean;
  axis?: 'left' | 'right';
  width?: number; // line width
  dash?: 'solid' | 'dashed' | 'dotted' | 'dashdot';
  curve?: 'smooth' | 'straight' | 'step';
  marker?: 'none' | 'circle' | 'square' | 'diamond' | 'triangle';
  markerSize?: number;
  opacity?: number; // fill, 10-100
  gradient?: boolean;
  labels?: 'inherit' | 'on' | 'off';
  labelPosition?: LabelPosition;
  labelColor?: string;
  altColor?: string; // per-point second colour (e.g. Consumed milk)
  trend?: 'none' | 'linear' | 'moving';
  trendPeriod?: number;
}

export interface ChartSettings {
  title?: string;
  showTitle?: boolean;
  height?: number;
  background?: string;
  border?: boolean;
  fontSize?: number;
  animation?: boolean;
  dataTable?: boolean;
  legend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltip?: boolean;
  gridH?: boolean;
  gridV?: boolean;
  gridDashed?: boolean;
  gridColor?: string;
  majorEvery?: number | null; // a stronger line every N on the left axis
  xShow?: boolean;
  xTitle?: string;
  xAngle?: number;
  xAllLabels?: boolean;
  xReverse?: boolean;
  left?: AxisSettings;
  right?: AxisSettings;
  stacking?: 'stacked' | 'clustered' | 'percent';
  groupGap?: number; // % gap between groups of bars
  barGap?: number; // px between bars in a group (negative = overlap)
  barRadius?: number;
  maxBarSize?: number;
  labelMode?: LabelMode;
  labelSize?: number;
  labelDecimals?: number | null;
  labelHalo?: boolean;
  showTotal?: boolean;
  refLines?: string; // "4.5 Target, 3.5"
  refColor?: string;
  series?: Record<string, SeriesSettings>;
}

export type AllChartSettings = Record<string, ChartSettings>;

const CACHE_KEY = 'ilms.charts';

function readCache(): AllChartSettings {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

let current: AllChartSettings = readCache();
const listeners = new Set<() => void>();

function publish(next: AllChartSettings): void {
  current = next;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked - still applies for this visit.
  }
  listeners.forEach((l) => l());
}

export function getAllChartSettings(): AllChartSettings {
  return current;
}

export function useAllChartSettings(): AllChartSettings {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
}

/** After sign-in: pick up the farm's chart settings. */
export async function syncChartSettings(): Promise<void> {
  try {
    const { data } = await apiClient.get<{ charts: AllChartSettings }>('/system-settings/appearance/charts');
    if (JSON.stringify(data.charts) !== JSON.stringify(current)) publish(data.charts ?? {});
  } catch {
    // Offline - keep the cached settings.
  }
}

export async function saveChartSettings(all: AllChartSettings): Promise<void> {
  const { data } = await apiClient.put<{ charts: AllChartSettings }>('/system-settings/appearance/charts', { charts: all });
  publish(data.charts ?? {});
}

/** Drops empty values so only real changes are saved. */
export function pruneSettings(s: ChartSettings): ChartSettings {
  const clean = (o: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(o)) {
      if (v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))) continue;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const inner = clean(v as Record<string, unknown>);
        if (Object.keys(inner).length) out[k] = inner;
      } else {
        out[k] = v;
      }
    }
    return out;
  };
  return clean(s as Record<string, unknown>) as ChartSettings;
}
